import { FC, useCallback } from 'react';

import {
  Data,
  DATA_DATE_FORMAT,
  DATA_NUMBER_FORMAT,
  DATA_STRING_FORMAT,
  DATA_TYPE,
} from 'utils/types';
import dayjs from 'dayjs';
import { Input, RadioGroup, Radio, Checkbox, Switch } from 'ui/components';
import {
  Textarea,
  PhoneNumber,
  DateField,
  Currency,
  MultiSelect,
  Select,
} from 'ui/components/form';
import { CheckboxGroup } from 'ui/components/form/Checkbox/Checkbox';
import { SIZE } from 'utils/constants';
import { isNil } from 'lodash';

import { LEAD_FORM } from './schema';

type Props = {
  field: Data;
  customData: LEAD_FORM['data'];
  handleUpdateCustomField: (dataId: string, value: any, field: Data) => void;
  disabled: boolean;
  hideLabel?: boolean;
  size?: SIZE;
};

const BaseCustomFieldFormField: FC<Props> = ({
  hideLabel,
  customData,
  field,
  handleUpdateCustomField,
  size = SIZE.Medium,
  disabled,
}) => {
  const ruleChecker = useCallback(
    (
      error: string,
    ): {
      textType: 'danger' | 'default' | 'success' | 'warning' | undefined;
      subText: string;
    } => {
      return {
        textType: error ? 'danger' : 'default',
        subText: error,
      };
    },
    [],
  );

  const renderField = useCallback(
    (field: Data) => {
      const fieldData = customData?.find((d) => d.dataId === field._id);

      switch (field.type) {
        case DATA_TYPE.SELECT:
          return (
            <Select
              key={field._id}
              options={field?.meta?.options ?? []}
              className="w-full"
              size={size}
              onChange={(e) =>
                handleUpdateCustomField(field._id, e.value, field)
              }
              placeholder={`Select ${field?.name}`}
              label={hideLabel ? '' : field.name}
              disabled={disabled}
              defaultValue={fieldData?.value as any}
            />
          );
        case DATA_TYPE.STRING:
          if (field.meta?.format === DATA_STRING_FORMAT.LONGSTRING) {
            return (
              <Textarea
                label={hideLabel ? '' : field.name}
                value={(fieldData?.value as any) ?? ''}
                onChange={(val) =>
                  handleUpdateCustomField(field._id, val, field)
                }
                rows={3}
                disabled={disabled}
                {...ruleChecker(fieldData?.error ?? '')}
              />
            );
          }

          return (
            <Input
              key={field._id}
              className="w-full"
              type="text"
              size={size}
              onChange={(e) => {
                handleUpdateCustomField(field._id, e.target.value, field);
              }}
              value={(fieldData?.value as any) ?? ''}
              label={hideLabel ? '' : field.name}
              disabled={disabled}
              {...ruleChecker(fieldData?.error ?? '')}
            />
          );
        case DATA_TYPE.MOBILENUMBER:
          return (
            <PhoneNumber
              key={field._id}
              className="w-full"
              name="mobileNumber"
              size={size}
              onChange={(_, val) => {
                handleUpdateCustomField(field._id, val, field);
              }}
              value={(fieldData?.value as any) ?? ''}
              label={hideLabel ? '' : field.name}
              disabled={disabled}
            />
          );
        case DATA_TYPE.DATE: {
          const _value =
            field.meta.format === DATA_DATE_FORMAT.DATE
              ? dayjs(fieldData?.value as Date).format('MM/DD/YYYY')
              : dayjs(fieldData?.value as Date).format('MM/DD/YYYY hh:mm:A');
          return (
            <DateField
              key={field._id}
              label={hideLabel ? '' : field.name}
              showTimeSelect={field?.meta?.format !== DATA_DATE_FORMAT.DATE}
              selected={
                fieldData?.value && dayjs(_value).isValid()
                  ? new Date(_value)
                  : undefined
              }
              name="date"
              dateFormat={
                field?.meta?.format === DATA_DATE_FORMAT.DATE
                  ? 'MM/dd/yyyy'
                  : 'MM/dd/yyyy hh:mm a'
              }
              onChange={(_e, value) => {
                handleUpdateCustomField(
                  field._id,
                  dayjs(value as any).toDate(),
                  field,
                );
              }}
              clearSelected={() => {
                handleUpdateCustomField(field._id, null, field);
              }}
            />
          );
        }
        case DATA_TYPE.RADIOBOX:
          return (
            <RadioGroup
              groupLabel={hideLabel ? '' : field.name}
              groupClassName="flex gap-3 flex-wrap required"
            >
              {field.meta.options?.map((p) => (
                <Radio
                  name={field.name}
                  label={p.label}
                  value={p.value}
                  key={p.value}
                  checked={p.value === fieldData?.value}
                  defaultChecked={p.value === fieldData?.value}
                  onClick={() =>
                    handleUpdateCustomField(field._id, p.value, field)
                  }
                  disabled={disabled}
                />
              ))}
            </RadioGroup>
          );
        case DATA_TYPE.NUMBER:
          switch (field.meta.format) {
            case DATA_NUMBER_FORMAT.FORMATTED:
              return (
                <Currency
                  allowNegativeValue
                  value={fieldData?.value as any}
                  decimalsLimit={10}
                  hidePrefix
                  label={hideLabel ? '' : field.name}
                  onValueChange={(value) =>
                    handleUpdateCustomField(
                      field._id,
                      !isNil(value) ? +value : null,
                      field,
                    )
                  }
                  disabled={disabled}
                  size={size}
                  {...ruleChecker(fieldData?.error ?? '')}
                />
              );
            case DATA_NUMBER_FORMAT.UNFORMATTED:
              return (
                <Input
                  label={hideLabel ? '' : field.name}
                  value={fieldData?.value as any}
                  type="number"
                  size={size}
                  onChange={(e) =>
                    handleUpdateCustomField(
                      field._id,
                      !isNil(e.target.value) ? +e.target.value : null,
                      field,
                    )
                  }
                  disabled={disabled}
                  {...ruleChecker(fieldData?.error ?? '')}
                />
              );
            case DATA_NUMBER_FORMAT.CURRENCY:
              return (
                <Currency
                  allowNegativeValue
                  value={fieldData?.value as any}
                  decimalsLimit={10}
                  label={hideLabel ? '' : field.name}
                  onValueChange={(value) =>
                    handleUpdateCustomField(
                      field._id,
                      !isNil(value) ? +value : null,
                      field,
                    )
                  }
                  disabled={disabled}
                  size={size}
                  {...ruleChecker(fieldData?.error ?? '')}
                />
              );
            case DATA_NUMBER_FORMAT.PERCENTAGE:
              return (
                <Currency
                  allowNegativeValue
                  value={fieldData?.value as any}
                  decimalsLimit={10}
                  label={hideLabel ? '' : field.name}
                  onValueChange={(value) =>
                    handleUpdateCustomField(
                      field._id,
                      !isNil(value) ? +value : null,
                      field,
                    )
                  }
                  disabled={disabled}
                  size={size}
                  hidePrefix
                  suffix="%"
                  {...ruleChecker(fieldData?.error ?? '')}
                />
              );
            default:
              return <div></div>;
          }
        case DATA_TYPE.CHECKBOX:
          return (
            <CheckboxGroup
              groupLabel={hideLabel ? '' : field.name}
              groupClassName="flex gap-3 flex-wrap required"
            >
              {field?.meta?.options?.map((opt) => {
                const isSelected = (
                  (fieldData?.value as string[]) ?? []
                ).includes(opt.value);

                const newValue = () => {
                  if (isSelected) {
                    return (
                      ((fieldData?.value as string[]) ?? []).filter(
                        (v: string) => v !== opt.value,
                      ) ?? []
                    );
                  } else {
                    return [
                      ...((fieldData?.value as string[]) ?? []),
                      opt.value,
                    ];
                  }
                };

                return (
                  <Checkbox
                    checked={!!isSelected}
                    label={hideLabel ? '' : opt.label}
                    value={opt.value}
                    onChange={() => {
                      handleUpdateCustomField(field._id, newValue(), field);
                    }}
                    disabled={disabled}
                  />
                );
              })}
            </CheckboxGroup>
          );
        case DATA_TYPE.MULTISELECT:
          return (
            <MultiSelect
              size={size}
              label={hideLabel ? '' : field.name}
              onChange={(opt) =>
                handleUpdateCustomField(
                  field._id,
                  opt.map((o) => o.value),
                  field,
                )
              }
              defaultValue={(fieldData?.value as any[]) ?? []}
              options={field.meta?.options ?? []}
              disabled={disabled}
            />
          );
        case DATA_TYPE.BOOLEAN:
          return (
            <Switch
              checked={!!fieldData?.value}
              label={hideLabel ? '' : field.name}
              onClick={() =>
                handleUpdateCustomField(field._id, !fieldData?.value, field)
              }
              disabled={disabled}
            />
          );
        default:
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customData, disabled],
  );
  return renderField(field);
};

export default BaseCustomFieldFormField;
